import i18n from 'i18next';

import { CURRENCY_FORMAT } from '~/core/constants';
import formatUOM from '~/core/helpers/formatUOM';

/**
 * Format a price and add a unit string (if avaialble)
 * @param price the price to format
 * @param unit the unit to append
 * @returns a formatted price string with currency and unit
 */
export const formatPriceWithUnit = (
  price: number,
  unit?: string | null
): string => {
  const formattedUnit = unit ? ` ${formatUOM(unit)}` : '';

  return CURRENCY_FORMAT.format(price) + formattedUnit;
};

/**
 * Formats a price with optional unit of measurement or displays "Free" for zero prices.
 *
 * @param price - The numeric price value to format (can be null or undefined)
 * @param unit - The unit of measurement to append (e.g., "sqft", "linear_ft")
 * @param showFreeIfZeroPrice - Whether to display "Free" for zero prices (defaults to false)
 * @param customFreeLabel - Custom label to display (defaults to "Free") if zero price
 * @returns Formatted price string with currency and unit, or localized "Free" text
 *
 * @example
 * formatPriceUnitOrFree(25.99, "sqft") // "$25.99 sq ft"
 * formatPriceUnitOrFree(0, "linear_ft", true) // "Free"
 * formatPriceUnitOrFree(0, "linear_ft", true, "Some Other Text") // "Some Other Text"
 * formatPriceUnitOrFree(0, "sqft") // "$0.00 sq ft"
 * formatPriceUnitOrFree(100) // "$100.00"
 */
export const formatPriceUnitOrFree = (
  price?: number | null,
  unit?: string | null,
  showFreeIfZeroPrice = false,
  customFreeLabel = i18n.t('COMMON.FREE_PRICE')
): string => {
  // Convert price to number, defaulting to 0 for null/undefined/NaN values
  const normalizedPrice = Number(price) || 0;

  // If price is zero and showFreeIfZeroPrice is true, return the appropriate "Free" label
  const showFreeLabel = !normalizedPrice && showFreeIfZeroPrice;

  return showFreeLabel
    ? customFreeLabel
    : formatPriceWithUnit(normalizedPrice, unit);
};
